import { Lightning } from '@lightningjs/sdk';
import { getLayoutTheme } from '../utils/theme';
import { backToBuffer, cleanupBuffer, getFromBuffer, PAGE_STATE, PLACEHOLDER } from '../utils/virtualization';
import { cancelPlacement, schedulePlacement } from '../utils/placement';
import { isImpressionTrackingEnabled } from '../utils/impression';
import {
  DETACHED_SIGNAL,
  FOCUSED_SIGNAL,
  IMPRESSION_TRACK_SIGNAL,
  IMPRESSION_UNTRACK_SIGNAL,
  INACTIVE_SIGNAL,
  PORTION_REQUESTED_SIGNAL,
  SELECTED_SIGNAL,
  SHOW_GRID_SIGNAL,
} from '../signals';
import { FOCUS_MODE_CENTER, FOCUS_MODES, START_POINT_MODE_LEFT, START_POINT_MODES } from './row.constants';
import { defaultContext, CONTENT_GAP } from './row.style';

export class Row extends Lightning.Component {
  static _template() {
    return {
      Container: {},
      passSignals: {
        [SELECTED_SIGNAL]: true,
        [SHOW_GRID_SIGNAL]: true,
        [PORTION_REQUESTED_SIGNAL]: true,
        [IMPRESSION_TRACK_SIGNAL]: true,
        [IMPRESSION_UNTRACK_SIGNAL]: true,
      },
    };
  }

  _construct() {
    this._style = defaultContext;
    this._contentGap = CONTENT_GAP[this._style.screenSize];
    this._isRowActive = false;
    this._isRowAttached = false;
    this._resetVirtualization();
    this._leftPadding = 0;
    this._rightPadding = 0;
  }

  _active() {
    this._isRowActive = true;

    if (!this._data) {
      return;
    }

    if (this._isDataApplied) {
      this._placeItems();

      return;
    }

    // The first data application is deferred to the placement scheduler, so a row the user scrolls
    // past never gets to spend a whole frame on items nobody is going to look at
    schedulePlacement(this);
  }

  _inactive() {
    this._isRowActive = false;
    cancelPlacement(this);
    // The queue is rebuilt from scratch on the way back in, so leaving a partially drained one behind would
    // strand the unplaced items: stepPlacement bails while inactive and nothing would pick them up again
    this._pending.length = 0;
    this._pendingCursor = 0;
  }

  _attach() {
    this._isRowAttached = true;
  }

  _detach() {
    this._isRowAttached = false;
    cancelPlacement(this);
    this._resetVirtualization();
  }

  /**
   * Performs a single unit of deferred work, driven by the placement scheduler
   *
   * @returns {boolean} whether there is more work left
   */
  stepPlacement() {
    if (!this._isRowActive || !this._data) {
      return false;
    }

    if (!this._isDataApplied) {
      // Applying the data queues the placement of the visible window
      this.data = this._data;

      return this._hasPendingItems();
    }

    if (this._hasPendingItems()) {
      this._placeItem(this._pending[this._pendingCursor]);
      this._pendingCursor += 1;
    }

    return this._hasPendingItems();
  }

  set data(value) {
    if (!value) {
      return;
    }

    this.patch({
      w: value.rowSize.w,
      h: value.itemSize.h,
    });
    this._data = value;

    if (!this._isRowActive) {
      return;
    }

    const { block, blockIndex, itemSize, theme } = this._data;
    const { items, pagination } = block.content;
    const { colorScheme } = getLayoutTheme(block.theme, theme);
    const isUpdating = this._isDataApplied;
    this._blockIndex = blockIndex;
    this._isDataApplied = true;
    this._colorScheme = colorScheme;
    this._id = block.id;
    this._itemSize = itemSize;
    // NOTE: the item wrappers are built by _getItemData on demand, a paginated row holds hundreds of items
    // but only ever displays about a dozen of them
    this._sourceItems = items;
    if (this._items.length > items.length) {
      this._items.length = items.length;
    }

    const { totalItems, itemsPerPage } = pagination;
    this._totalItems = totalItems;
    this._itemsPerPage = itemsPerPage;
    this._pages = block.pages;

    this._updateContentPlacementStartPoint();
    this._setIndex(Math.min(this._focusedItemIndex, Math.max(this._totalItems - 1, 0)), false, true);
    // Remove extra items in case of a row data update
    if (isUpdating) {
      const views = Array.from(this._views.keys());
      for (let i = 0; i < views.length; i += 1) {
        const viewIndex = views[i];
        if (viewIndex >= this._totalItems) {
          this._backToBuffer(this._views.get(viewIndex));
        }
      }
    }

    this._refocus();
  }

  get id() {
    return this._id;
  }

  set focusMode(value) {
    if (FOCUS_MODES.includes(value) && this._focusMode !== value) {
      this._focusMode = value;
      this.goTo(this._focusedItemIndex);
    }
  }

  get startPointMode() {
    return this._startPointMode;
  }

  set startPointMode(value) {
    if (START_POINT_MODES.includes(value) && this._startPointMode !== value) {
      this._startPointMode = value;
      this._updateContentPlacementStartPoint();
      this.goTo(this._focusedItemIndex, true);
    }
  }

  get contentGap() {
    return this._contentGap;
  }

  set contentGap(value) {
    this._contentGap = value;
    this._updateContentPlacementStartPoint();
    this.goTo(this._focusedItemIndex, true);
  }

  get leftPadding() {
    return this._leftPadding;
  }

  set leftPadding(value) {
    this._leftPadding = value;
    this._updateContentPlacementStartPoint();
    this.goTo(this._focusedItemIndex, true);
  }

  get rightPadding() {
    return this._rightPadding;
  }

  set rightPadding(value) {
    this._rightPadding = value;
    this._updateContentPlacementStartPoint();
    this.goTo(this._focusedItemIndex, true);
  }

  get focusedIndex() {
    return this._focusedItemIndex;
  }

  set focusedIndex(value) {
    this._focusedItemIndex = value;
  }

  goTo(index, shouldRefresh = false) {
    this._setIndex(index, false, shouldRefresh);
  }

  _setIndex(index, shouldAnimate = false, shouldRefresh = false) {
    this._focusedItemIndex = index;

    if (!this._totalItems) {
      return;
    }

    if (shouldAnimate) {
      this._animateTo(this._focusedItemIndex);
    } else {
      this._moveTo(this._focusedItemIndex);
    }

    if (this._isRowActive) {
      this._placeItems(shouldRefresh);
    }

    this.signal(FOCUSED_SIGNAL, index);

    if (this._itemsPerPage > 0 && this._pages) {
      const currentPage = Math.floor(this._focusedItemIndex / this._itemsPerPage);
      // NOTE: the first page will be empty in case if the Row is used within a Selector block
      const nextPage = this._pages[0] === PAGE_STATE.LOADED ? currentPage + 1 : 0;

      if (currentPage !== nextPage) {
        this._requestPage(currentPage);
        // In case the row was restored with anchoring from a backward navigation
        if (currentPage - 1 >= 0) {
          this._requestPage(currentPage - 1);
        }
      }

      this._requestPage(nextPage);
    }
  }

  /**
   * Sends a signal requesting a specific page of items
   *
   * @param {Number} page zero-based page index
   */
  _requestPage(page) {
    if (this._pages[page] !== PAGE_STATE.MISSING) {
      return;
    }

    this.signal(PORTION_REQUESTED_SIGNAL, {
      // NOTE: BFF API requires the pages to be 1-based
      page: page + 1,
      start: page * this._itemsPerPage,
      id: this.id,
    });
  }

  _focus() {
    // The focused row has to be usable right away, so it does not wait for its scheduled turn
    if (!this._isDataApplied && this._data) {
      this.data = this._data;

      return;
    }

    // Handle a case when some items disappearing on a far row edge upon sidenav opening
    this._placeItems();
  }

  _getFocused() {
    return this._views.get(this._focusedItemIndex);
  }

  _makeItemData(content, index) {
    const { itemSize, itemTemplate, renderingContext } = this._data;

    return {
      content,
      index,
      config: itemSize,
      template: itemTemplate,
      colorScheme: this._colorScheme,
      renderingContext,
    };
  }

  /**
   * Builds, or refreshes in place, the item wrapper of a single index
   *
   * @param {Number} index zero-based item index
   * @returns {Object} the item wrapper, carrying a PLACEHOLDER while the content is not loaded yet
   */
  _getItemData(index) {
    const content = this._sourceItems[index] || PLACEHOLDER;
    const existingItem = this._items[index];

    if (!existingItem) {
      this._items[index] = this._makeItemData(content, index);

      return this._items[index];
    }

    // NOTE: It's faster than creation of a new object
    existingItem.content = content;
    existingItem.config = this._data.itemSize;
    existingItem.template = this._data.itemTemplate;
    existingItem.colorScheme = this._colorScheme;
    existingItem.renderingContext = this._data.renderingContext;

    return existingItem;
  }

  _getMaxOffset() {
    if (!this._data || !this._totalItems) {
      return 0;
    }

    const itemWidthWithGap = this._data.itemSize.w + this._contentGap;
    const allItemsSize = this._totalItems * itemWidthWithGap - this._contentGap;

    return this.finalW - this._leftPadding - this._rightPadding - allItemsSize;
  }

  _updateContentPlacementStartPoint() {
    if (!this._data || this._startPointMode === START_POINT_MODE_LEFT) {
      this._startPoint = 0;

      return;
    }

    this._startPoint = Math.max(Math.round(this._getMaxOffset() / 2), 0);
  }

  _offsetByIndex(index) {
    if (!this._data || !this._totalItems) {
      return 0;
    }

    const itemWidthWithGap = this._data.itemSize.w + this._contentGap;
    const maxOffset = Math.min(this._getMaxOffset(), 0);
    const offset = -index * itemWidthWithGap;

    if (this._focusMode === FOCUS_MODE_CENTER) {
      return Math.max(
        Math.min(0, offset + Math.round(this.finalW - this._data.itemSize.w) / 2 - this._leftPadding),
        maxOffset,
      );
    }

    return Math.max(Math.min(0, offset), maxOffset);
  }

  _animateTo(index) {
    this._offset = this._offsetByIndex(index);
    this.tag('Container').setSmooth('x', this._offset);
  }

  _moveTo(index) {
    this._offset = this._offsetByIndex(index);
    this.tag('Container').x = this._offset;
  }

  _handleLeft() {
    if (this._focusedItemIndex > 0) {
      this._setIndex(this._focusedItemIndex - 1, true);

      return true;
    }

    return false;
  }

  _handleRight() {
    if (this._focusedItemIndex < this._totalItems - 1) {
      this._setIndex(this._focusedItemIndex + 1, true);

      return true;
    }

    return false;
  }

  _getFromBufferOrCreate(contentId) {
    const { itemType, itemSize, itemTemplate } = this._data;

    const existingView = getFromBuffer(itemTemplate, contentId);
    if (existingView) {
      return existingView;
    }

    return this.stage.c({
      type: itemType,
      w: itemSize.w,
      h: itemSize.h,
      template: itemTemplate,
      passSignals: {
        [SELECTED_SIGNAL]: true,
      },
      signals: {
        [INACTIVE_SIGNAL]: '_onViewBecomeInactive',
        [DETACHED_SIGNAL]: '_onViewBecomeDetached',
      },
    });
  }

  _onViewBecomeInactive(view) {
    if (this._isRowAttached) {
      this._backToBuffer(view);
    }
  }

  _onViewBecomeDetached(view) {
    cleanupBuffer(view);
  }

  _backToBuffer(view) {
    // NOTE: _placeItem registers a view under the index it just assigned to it, so there is no need to scan
    if (this._views.get(view.index) === view) {
      this._views.delete(view.index);
      this._staleIndices.delete(view.index);
    }

    backToBuffer(view);
    view.visible = false;
  }

  _getItemX(index) {
    return this._startPoint + (this._data.itemSize.w + this._contentGap) * index;
  }

  _placeItems(shouldRefresh = false) {
    if (!this._data || !this._totalItems) {
      return;
    }

    const viewportWidth = this.finalW;
    const itemWidthWithGap = this._data.itemSize.w + this._contentGap;
    const maximalOffset = this._focusedItemIndex * itemWidthWithGap;
    const left = maximalOffset + this._offset + this._leftPadding;
    const right = viewportWidth - left;
    const itemsLeft = Math.ceil(left / itemWidthWithGap);
    const itemsRight = Math.ceil(right / itemWidthWithGap);
    const startIndex = Math.max(this._focusedItemIndex - itemsLeft, 0);
    const endIndex = Math.min(this._focusedItemIndex + itemsRight, this._totalItems - 1);

    if (shouldRefresh) {
      // The content behind the placed views has changed, so each has to take it again when it is next reached
      this._views.forEach((view, index) => this._staleIndices.add(index));
    }

    this._trackImpressions(startIndex, endIndex, viewportWidth);

    // The focused view must exist synchronously, _getFocused() relies on it
    if (!this._views.has(this._focusedItemIndex) || this._staleIndices.has(this._focusedItemIndex)) {
      this._placeItem(this._focusedItemIndex);
    }

    // The rest is queued from the focus outwards, so the items closest to the eye are placed first
    this._pending.length = 0;
    this._pendingCursor = 0;

    const maxDistance = Math.max(this._focusedItemIndex - startIndex, endIndex - this._focusedItemIndex);
    for (let distance = 1; distance <= maxDistance; distance += 1) {
      const next = this._focusedItemIndex + distance;
      const previous = this._focusedItemIndex - distance;

      // An item keeps its x whatever the focus does, so one already in place is left alone unless stale
      if (next <= endIndex && (!this._views.has(next) || this._staleIndices.has(next))) {
        this._pending.push(next);
      }

      if (previous >= startIndex && (!this._views.has(previous) || this._staleIndices.has(previous))) {
        this._pending.push(previous);
      }
    }

    if (this._hasPendingItems()) {
      schedulePlacement(this);
    }
  }

  _hasPendingItems() {
    return this._pendingCursor < this._pending.length;
  }

  _placeItem(index) {
    const itemData = this._getItemData(index);
    const container = this.tag('Container');

    let view = this._views.get(index);
    if (!view) {
      view = this._getFromBufferOrCreate(itemData.content.id);
    }

    // NOTE: re-parenting has to happen before the data is applied, since it detaches the view and a detached
    // content template drops the item it is displaying
    if (view.parent !== container) {
      container.childList.add(view);
    }

    view.index = index;
    view.item = itemData;
    view.x = this._getItemX(index);
    view.visible = true;
    this._views.set(index, view);
    this._staleIndices.delete(index);
  }

  _trackImpressions(startIndex, endIndex, viewportWidth) {
    if (!isImpressionTrackingEnabled()) {
      return;
    }

    const impressionLeft = -this._offset;
    const impressionRight = impressionLeft + viewportWidth - this._leftPadding - this._contentGap;
    const tracked = [];
    const untracked = [];

    for (let index = startIndex; index <= endIndex; index += 1) {
      const x = this._getItemX(index);

      if (this._sourceItems[index] && x >= impressionLeft && x + this._data.itemSize.w <= impressionRight) {
        tracked.push(index);
      } else {
        untracked.push(index);
      }
    }

    if (tracked.length) {
      this.signal(IMPRESSION_TRACK_SIGNAL, this._blockIndex, tracked);
    }

    if (untracked.length) {
      this.signal(IMPRESSION_UNTRACK_SIGNAL, this._blockIndex, untracked);
    }
  }

  _resetVirtualization() {
    this._itemBlueprint = {};
    this._data = undefined;
    this._isDataApplied = false;
    this._focusedItemIndex = 0;
    this._offset = 0;
    this._itemsPerPage = 0;
    this._startPoint = 0;
    this._items = [];
    this._sourceItems = [];
    this._views = new Map();
    this._pages = {};
    this._pending = [];
    this._pendingCursor = 0;
    this._staleIndices = new Set();
    this._focusMode = FOCUS_MODE_CENTER;
    this._startPointMode = START_POINT_MODE_LEFT;
  }
}
